@if (isOpen()) {
  <div class="modal-backdrop" (click)="closeModal()"></div>
}

@if (isOpen()) {
  <div class="modal-container" (click)="$event.stopPropagation()">
    <div class="modal-content">
      <!-- Modal Header -->
      <div class="modal-header">
        <h2 class="text-xl font-semibold text-gray-800">Add Insights to Canvas</h2>
        <button (click)="closeModal()" class="p-1 rounded-full hover:bg-gray-100">
          <ui-icon name="x" class="stroke-gray-500 size-5"></ui-icon>
        </button>
      </div>

      <!-- Tabs -->
      <div class="tabs-container">
        <button
          [class.active-tab]="activeTab() === 'reports'"
          (click)="switchTab('reports')"
          class="tab-button">
          <ui-icon name="reports" class="mr-2" size="size-5"></ui-icon>
          Reports
        </button>
        <button
          [class.active-tab]="activeTab() === 'conversations'"
          (click)="switchTab('conversations')"
          class="tab-button">
          <ui-icon name="data-copilot" class="mr-2" size="size-5"></ui-icon>
          Conversations
        </button>
      </div>

      <!-- Tab Content -->
      <div class="tab-content">
        <!-- Reports Tab -->
        <div [hidden]="activeTab() !== 'reports'">
          @if (!loadingReports() && filteredReports().length === 0) {
            <div class="flex items-center justify-center h-64">
              <div class="text-center">
                <ui-icon name="reports" class="mx-auto mb-4" size="size-12"></ui-icon>
                <p class="text-gray-500 mb-2">No reports found in canvas</p>
                <p class="text-gray-400 text-sm">Only reports added to canvas will appear here</p>
              </div>
            </div>
          }

          @if (!loadingReports() && filteredReports().length > 0) {
            <app-assets-flat-list-table
              [listType]="listType()"
              [assetType]="'REPORT'"
              [assetsInput]="filteredReports()"
              [hideDateFilter]="true"
              [assetHeight]="280"
              [showSelectionCheckbox]="true"
              [singleSelectionMode]="true"
              (changeListType)="onChangeListType($event)"
              (selectedItemsChange)="handleReportSelection($event)">
            </app-assets-flat-list-table>
          }

          @if (loadingReports()) {
            <ui-skeleton-table></ui-skeleton-table>
          }
        </div>

        <!-- Conversations Tab -->
        @if (activeTab() === 'conversations') {
          <div class="conversations-tab">
            @if (loadingConversations()) {
              <div class="flex justify-center items-center py-8">
                <div class="animate-pulse flex flex-col w-full space-y-4">
                  <div class="h-8 bg-gray-200 rounded w-3/4"></div>
                  <div class="h-8 bg-gray-200 rounded w-5/6"></div>
                  <div class="h-8 bg-gray-200 rounded w-4/5"></div>
                </div>
              </div>
            }

            @if (!loadingConversations() && filteredConversations().length === 0) {
              <div class="flex items-center justify-center h-64">
                <div class="text-center">
                  <ui-icon name="data-copilot" class="mx-auto mb-4" size="size-12"></ui-icon>
                  <p class="text-gray-500 mb-2">No conversations found in canvas</p>
                  <p class="text-gray-400 text-sm">Only conversations added to canvas will appear here</p>
                </div>
              </div>
            }

            @if (!loadingConversations() && filteredConversations().length > 0) {
              <div class="conversation-list">
                @for (conversation of filteredConversations(); track conversation.id) {
                  <div class="conversation-item">
                    <!-- Conversation Header -->
                    <div class="conversation-header" (click)="toggleConversation(conversation.id, $event)">
                      <div class="expand-button">
                        @if (isConversationExpanded(conversation.id)) {
                          <ui-icon
                            name="chevron-down"
                            class="stroke-[#999999] stroke-[2.5px] cursor-pointer hover:stroke-blue-700"
                            size="size-4">
                          </ui-icon>
                        } @else {
                          <ui-icon
                            name="chevron-right"
                            class="stroke-[#999999] stroke-[2.5px] cursor-pointer hover:stroke-blue-700"
                            size="size-4">
                          </ui-icon>
                        }
                      </div>
                      <div class="conversation-title">
                        @if (conversation.parameters?.pinned) {
                          <span class="pinned-icon" title="Pinned conversation">
                            <ui-icon name="pin" size="size-3" class="text-blue-500 mr-1"></ui-icon>
                          </span>
                        }
                        {{ conversation.name || 'Untitled Conversation' }}
                      </div>
                    </div>

                    <!-- Expanded conversation content with questions -->
                    @if (isConversationExpanded(conversation.id)) {
                      <div class="conversation-content">
                        @if (!isQuestionLoading(conversation.id) && hasQuestions(conversation.id)) {
                          <div class="questions-list">
                            @for (question of getQuestionsForConversation(conversation.id); track question.id) {
                              <div
                                class="question-item"
                                [class.selected]="isQuestionSelected(question.id)"
                                (click)="selectQuestion(question, $event)">
                                <div class="question-content">
                                  <div class="checkbox-wrapper">
                                    <input
                                      type="radio"
                                      name="selectedQuestion"
                                      [checked]="isQuestionSelected(question.id)"
                                      (click)="$event.stopPropagation()"
                                      (change)="selectQuestion(question, $event)"
                                      class="form-radio"
                                      aria-label="Select question"
                                    />
                                  </div>
                                  <span class="question-text">{{ question.text }}</span>
                                </div>
                              </div>
                            }
                          </div>
                        }

                        @if (!isQuestionLoading(conversation.id) && !hasQuestions(conversation.id)) {
                          <div class="no-questions">
                            <ui-icon name="message-question" class="mx-auto mb-2 no-questions-icon" size="size-5"></ui-icon>
                            <p class="text-gray-500 text-sm">No questions found in this conversation</p>
                          </div>
                        }

                        @if (isQuestionLoading(conversation.id)) {
                          <div class="questions-loading">
                            <div class="animate-pulse flex flex-col space-y-3">
                              <div class="flex items-center space-x-3">
                                <div class="h-4 w-4 bg-gray-200 rounded-full"></div>
                                <div class="h-4 bg-gray-200 rounded w-3/4"></div>
                              </div>
                              <div class="flex items-center space-x-3">
                                <div class="h-4 w-4 bg-gray-200 rounded-full"></div>
                                <div class="h-4 bg-gray-200 rounded w-5/6"></div>
                              </div>
                              <div class="flex items-center space-x-3">
                                <div class="h-4 w-4 bg-gray-200 rounded-full"></div>
                                <div class="h-4 bg-gray-200 rounded w-4/5"></div>
                              </div>
                            </div>
                          </div>
                        }
                      </div>
                    }
                  </div>
                }
              </div>
            }
          </div>
        }
      </div>

      <!-- Modal Footer -->
      <div class="modal-footer">
        <div class="flex-1">
          @if (!hasSelection && !isGeneratingInsights()) {
            <span class="text-gray-500 text-sm">
              Select one report or conversation to add insights
            </span>
          }
          @if (isGeneratingInsights()) {
            <span class="text-blue-600 text-sm flex items-center">
              <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
              Generating insights...
            </span>
          }
          @if (insightsError()) {
            <span class="text-red-500 text-sm">
              {{ insightsError() }}
            </span>
          }
        </div>
        <button
          (click)="closeModal()"
          class="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          [disabled]="isGeneratingInsights()">
          Cancel
        </button>
        <button
          (click)="addInsightsToCanvas()"
          class="px-4 py-2 text-white rounded-md ml-2"
          [ngClass]="{
            'bg-blue-600 hover:bg-blue-700': !isAddButtonDisabled && !isGeneratingInsights(),
            'bg-gray-400 cursor-not-allowed': isAddButtonDisabled || isGeneratingInsights()
          }"
          [disabled]="isAddButtonDisabled || isGeneratingInsights()">
          @if (!isGeneratingInsights()) {
            Add Insights to Canvas
          }
          @if (isGeneratingInsights()) {
            Generating...
          }
        </button>
      </div>
    </div>
  </div>
}
